import { FocusEvent, ReactElement, ReactNode } from 'react'
import { Button, Form, Skeleton, Select, Spin, Flex } from 'antd'
import { SelectIProps } from './select-types'

const { Option } = Select

const SearchSelect = <T extends { id: string }>({
  dataHandleResult,
  labelRender,
  optionRender,
  formItemName,
  required,
  mode,
  placeHolder,
  onChangeGetItem,
  onSelectGetItem,
  onDeselectGetItem,
  selectProps,
  formItemProps,
  optionsItemProps,
}: SelectIProps<T>) => {
  const {
    viewData,
    setSelectData,
    setSearchItems,
    metaData,
    loadType,
    initFetch,
    loadMoreData,
    searchData,
    getInitData,
    fetchOnFocus,
  } = dataHandleResult

  const onChange = (ids: string | string[]) => {
    if (onChangeGetItem) {
      if (mode === 'multiple') {
        const callbackItems: T[] = []
        ;(ids as string[]).forEach((id) => {
          const item = viewData?.find((x) => x.id === id)
          if (item) {
            callbackItems.push(item)
          }
        })
        onChangeGetItem(callbackItems)
      } else {
        const itemId = ids as string
        const item = viewData?.find((x) => x.id === itemId)
        onChangeGetItem(item)
      }
    }
  }

  const onSelect = (id: string) => {
    if (onSelectGetItem) {
      onSelectGetItem(viewData?.find((x) => x.id === id))
    }
    const selected = viewData?.find((x) => x.id === id)
    if (selected)
      setSelectData((prev) =>
        prev.find((prevItem) => prevItem.id === selected.id)
          ? prev
          : [...prev, selected]
      )
  }

  const onDeselect = (id: string) => {
    if (onDeselectGetItem) {
      onDeselectGetItem(viewData?.find((x) => x.id === id))
    }
    setSelectData((prev) => prev.filter((prevItem) => prevItem.id !== id))
  }

  const onFocus = (e: FocusEvent<HTMLElement, Element>) => {
    // fetch data khi focus
    if (fetchOnFocus && initFetch === false) {
      getInitData()
    }
    // Thực thi các hành động khác nếu có
    if (!selectProps) {
      return
    }
    selectProps?.onFocus?.(e)
  }

  const onDropdownVisibleChange = (open: boolean) => {
    // Xóa search data khi đóng dropdown
    if (!open) {
      setSearchItems(undefined)
    }
    // Thực thi các hành động khác nếu có
    if (!selectProps) {
      return
    }
    selectProps?.onDropdownVisibleChange?.(open)
  }

  const onSearch = (value: string | undefined) => {
    if (!value) {
      searchData(undefined)
      return
    }
    searchData(value)
  }

  const dropDownFooter = metaData?.paging.cursor.next ? (
    <Flex
      onMouseDown={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      justify="center"
    >
      <Button
        type="link"
        loading={loadType === 'loadmore'}
        onClick={loadMoreData}
      >
        {loadType === 'loadmore' ? 'Đang tải...' : 'Tải thêm'}
      </Button>
    </Flex>
  ) : null

  const dropdownRender = (menu: ReactNode): ReactElement => (
    <Skeleton active loading={loadType === 'fetch'} paragraph={{ rows: 2 }}>
      {menu}
      {dropDownFooter}
    </Skeleton>
  )

  const selectRender = (
    <Select
      {...selectProps}
      mode={mode}
      filterOption={selectProps?.filterOption ?? false}
      showSearch={selectProps?.showSearch ?? true}
      placeholder={selectProps?.placeholder ?? placeHolder ?? 'Tìm dữ liệu'}
      dropdownRender={
        typeof selectProps?.dropdownRender === 'function'
          ? (menu) =>
              selectProps?.dropdownRender?.(
                dropdownRender(menu)
              ) as ReactElement
          : dropdownRender
      }
      onChange={selectProps?.onChange || onChange}
      onSelect={selectProps?.onSelect || onSelect}
      onSearch={selectProps?.onSearch || onSearch}
      onDeselect={selectProps?.onDeselect || onDeselect}
      onFocus={onFocus}
      onDropdownVisibleChange={onDropdownVisibleChange}
      optionLabelProp={selectProps?.optionLabelProp || 'label'}
      allowClear={
        loadType === 'search' ? false : selectProps?.allowClear ?? true
      }
      suffixIcon={
        loadType === 'search' ? <Spin size="small" /> : selectProps?.suffixIcon
      }
      style={{ width: '100%', ...selectProps?.style }}
    >
      {!selectProps?.options
        ? viewData.map((item) => (
            <Option
              key={item.id}
              value={optionsItemProps?.(item)?.value || item.id}
              label={optionsItemProps?.(item)?.label || labelRender(item)}
              disabled={optionsItemProps?.(item)?.disabled}
            >
              {optionRender?.(item) || labelRender(item) || item.id}
            </Option>
          ))
        : null}
    </Select>
  )

  return (
    <Form.Item
      {...formItemProps}
      name={formItemProps?.name || formItemName}
      required={formItemProps?.required ?? required ?? false}
      rules={formItemProps?.rules || [{ required, message: '' }]}
      style={{ width: '100%', ...formItemProps?.style }}
    >
      {selectRender}
    </Form.Item>
  )
}

export default SearchSelect
